import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';

export const ModalStyled = styled(motion.div)<{
  $zIndex?: number;
  $textAlign?: string;
  $width?: string;
  $modalOverflow?: string;
  $backgroundColor?: string;
  $inWrapper?: boolean;
}>`
  position: fixed;
  /* Centre of the VISIBLE viewport where a page publishes it (the chat pages,
     via useVisualViewportHeight): with the phone keyboard up, plain top: 50%
     is the centre of the layout viewport, which puts the lower half of the
     modal - and the field being typed into - behind the keys. Elsewhere the
     variable is absent and this is exactly top: 50%. */
  top: var(--app-vv-center, 50%);
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor ? $backgroundColor : theme.background};
  z-index: ${({ $zIndex }) => ($zIndex ? `${$zIndex + 1}` : '100')};
  text-align: ${({ $textAlign }) => ($textAlign ? $textAlign : 'center')};
  border-radius: 30px;
  width: ${({ $width }) => $width ?? '450px'};
  max-width: 90%;
  ${({ $modalOverflow }) => $modalOverflow && `overflow: ${$modalOverflow}`};

  > svg {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 800px) {
    max-width: calc(100% - 40px);

    > svg {
      width: 100px;
    }
  }

  /* When wrapped by ModalGlowWrapper (glow), the wrapper owns the centering and
     the width caps; the modal itself becomes an in-flow, opaque box that simply
     fills it, so the wrapper's blurred halo shows behind it. Kept LAST so the
     mobile max-width above doesn't shrink it inside the wrapper (which left the
     box narrower than its glow, i.e. visibly off-centre). */
  ${({ $inWrapper }) =>
    $inWrapper &&
    `
    position: relative;
    top: auto;
    left: auto;
    transform: none;
    width: 100%;
    max-width: 100%;

    @media (max-width: 800px) {
      max-width: 100%;
    }
  `}
`;

export const ModalScrollableStyled = styled(motion.div)<{
  $overflow?: string;
  $maxHeight?: string;
  $minHeight?: string;
  $maxHeightSM?: string;
  $minHeightSM?: string;
}>`
  max-height: calc(90vh - 85px);
  ${({ $maxHeight }) => $maxHeight && `max-height: ${$maxHeight}`};
  ${({ $minHeight }) => $minHeight && `min-height: ${$minHeight}`};

  overflow: ${({ $overflow }) => ($overflow ? $overflow : 'auto')};
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 800px) {
    ${({ $maxHeightSM }) => $maxHeightSM && `max-height: ${$maxHeightSM}`};
    ${({ $minHeightSM }) => $minHeightSM && `min-height: ${$minHeightSM}`};
  }
`;

export const ModalOverflowStyled = styled(motion.div)<{
  $logo?: boolean;
  $vDriveLogo?: boolean;
  $successImage?: boolean;
  $footer?: boolean;
  $numOfFloatingButtons?: number;
  $overflow?: string;
  $padding?: string;
  $paddingSM?: string;
}>`
  padding: ${({ $logo, $vDriveLogo, $successImage }) =>
    $logo || $vDriveLogo || $successImage ? '110px 24px 24px 24px' : '24px'};

  ${({ $footer, $numOfFloatingButtons }) =>
    $footer &&
    `${
      $numOfFloatingButtons === 1
        ? `padding-bottom: 109px;`
        : `padding-bottom: 170px;`
    }`}

  overflow: ${({ $overflow }) => ($overflow ? $overflow : 'hidden')};
  ${({ $padding }) => $padding && `padding: ${$padding};`}

  @media (max-width: 800px) {
    padding: ${({ $logo, $vDriveLogo, $successImage }) =>
      $logo || $vDriveLogo || $successImage ? '110px 24px 24px 24px' : '24px'};
    ${({ $footer, $numOfFloatingButtons }) =>
      $footer &&
      `${
        $numOfFloatingButtons === 1
          ? `padding-bottom: 109px;`
          : `padding-bottom: 170px;`
      }`}
    ${({ $paddingSM }) => $paddingSM && `padding: ${$paddingSM};`}
  }
`;

export const ModalOverlay = styled(motion.div)<{
  $zIndex?: number;
}>`
  background-color: ${({ theme }) => theme.darkGray}4D;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: ${({ $zIndex }) => ($zIndex ? `${$zIndex}` : '99')};
  /* backdrop-filter: blur(10px); */
`;

export const SuccessImage = styled(motion.img)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 250px;
`;

export const Image = styled(motion.img)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150px;
`;

/* A minimum, not a fixed height: a title that wraps (a long one on a phone)
   used to spill over the top of the red band and push the subtitle off its
   bottom. One-line headers keep the old 85/80px. */
export const ModalHeader = styled.div`
  background-color: ${({ theme }) => theme.primary};
  min-height: 85px;
  border-radius: 30px 30px 0 0;
  padding: 16px 56px 16px 32px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  text-align: left;

  @media (max-width: 800px) {
    min-height: 80px;
    padding: 16px 56px 16px 24px;
  }
`;

export const ModalFooter = styled.div`
  /* background-color: ${({ theme }) => theme.primary}; */
  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 1) 30%,
    rgba(255, 255, 255, 0) 100%
  );
  background: -webkit-linear-gradient(
    bottom,
    rgba(255, 255, 255, 1) 30%,
    rgba(255, 255, 255, 0) 100%
  );
  z-index: 2;

  position: absolute;
  width: 100%;
  left: 0;
  bottom: 0;
  right: 0;
  border-radius: 30px;
  padding: 24px;
  @media (max-width: 800px) {
    /* height: 80px; */
  }
`;

// --- AI "working" glow: animated gradient border + skeleton shimmer sweep.
// Ported from the Menu-AI preview (menuAiGlow.styled). Opt-in via <Modal glow>.
const flow = keyframes`
  from { background-position: 0% 50%; }
  to { background-position: 200% 50%; }
`;

const sweep = keyframes`
  from { transform: translateX(-130%); }
  to { transform: translateX(130%); }
`;

/**
 * The AI-brand gradient (blue → purple → magenta) with its first stop repeated
 * at the end so an animated `background-position` loops seamlessly. We animate
 * `background-position` (not a `@property` angle) so it works across the full
 * browserslist.
 */
const flowingGradient = css`
  background: ${({ theme }) => theme.aiGradient};
  background-size: 200% 100%;
  animation: ${flow} 3s linear infinite;
`;

/**
 * Transparent wrapper around the modal (used by modals that opt into `glow`).
 * Owns the fixed centering so the modal inside can be an in-flow opaque box,
 * and renders a soft blurred gradient HALO behind it (`z-index:-1`, filled +
 * blurred) — the "glow" from the Menu-AI preview's GlowWrapper. Kept on a
 * wrapper (not the modal) because the modal's opaque background would otherwise
 * cover it.
 *
 * The wrapper stays mounted for the whole life of such a modal and only the
 * halo is toggled via `$active` — mounting/unmounting it mid-flight would
 * remount the modal subtree and replay the open animation (a visible blink).
 */
export const ModalGlowWrapper = styled(motion.div)<{
  $zIndex?: number;
  $width?: string;
  $active?: boolean;
}>`
  position: fixed;
  /* Same visible-viewport centring as ModalStyled above. */
  top: var(--app-vv-center, 50%);
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${({ $zIndex }) => ($zIndex ? `${$zIndex + 1}` : '100')};
  width: ${({ $width }) => $width ?? '450px'};
  max-width: 90%;

  &::after {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: 40px;
    ${flowingGradient};
    filter: blur(20px);
    opacity: ${({ $active }) => ($active ? 0.55 : 0)};
    transition: opacity 0.3s ease;
    z-index: -1;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }

  @media (max-width: 800px) {
    max-width: calc(100% - 40px);
  }
`;

/**
 * Crisp animated gradient border hugging the modal's rounded box. Uses the
 * `mask-composite: exclude` recipe so only the 2px ring shows. `inset: 0` +
 * `border-radius: 30px` matches `ModalStyled`.
 */
export const ModalGlowBorder = styled.div<{ $active?: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: 30px;
  padding: 2px;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.3s ease;
  ${flowingGradient};
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 3;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * Moving glare/shimmer sweep across the modal (skeleton-style). Its own
 * `overflow: hidden` + `border-radius` keep the diagonal sweep clipped to the
 * rounded rect.
 */
export const ModalShimmer = styled.div<{ $active?: boolean }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: 30px;
  z-index: 2;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      100deg,
      transparent 35%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 65%
    );
    transform: translateX(-130%);
    animation: ${sweep} 2.2s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
      display: none;
    }
  }
`;
